<template>
    <Form ref="elFormObj" as="">
        <v-row v-if="system === 'imperial'" density="compact">
            <v-col>
                <Field
                    v-slot="{ field, errors }"
                    name="weightLb"
                    label="Weight (lbs)"
                    :model-value="weightLb"
                    :rules="{ required: system === 'imperial', min_value: 35, max_value: 500 }"
                >
                    <v-text-field
                        v-bind="field"
                        :model-value="field.value"
                        type="number"
                        min="35"
                        max="500"
                        :error-messages="errors"
                        @update:model-value="onChangeLb($event)"
                        name="weightLb"
                        variant="outlined"
                        required
                    ></v-text-field>
                </Field>
            </v-col>
        </v-row>
        <v-row v-if="system === 'metric'" density="compact">
            <v-col>
                <Field
                    name="weightKg"
                    label="Weight (kg)"
                    v-slot="{ field, errors }"
                    :model-value="weightKg"
                    :rules="{ required: system === 'metric', min_value: 15.88, max_value: 226.78 }"
                >
                    <v-text-field
                        v-bind="field"
                        :model-value="field.value"
                        type="number"
                        min="15.88"
                        max="226.78"
                        :error-messages="errors"
                        @update:model-value="onChangeKg($event)"
                        name="weightKg"
                        variant="outlined"
                        required
                    ></v-text-field>
                </Field>
            </v-col>
        </v-row>
    </Form>
</template>

<script lang="ts" setup>
    import type { Field, Form } from 'vee-validate';
    import { isNil as lo_isNil } from 'es-toolkit';
    import { isNumber as lo_isNumber } from 'es-toolkit/compat';
    import {
        convertLbToKg,
        convertGramsToKg,
        convertKgToGrams
    } from 'x1-base/utils';
    import { initVeeRules } from '~/helpers/forms';

    initVeeRules([ 'required', 'min_value', 'max_value' ]);


    const props = defineProps({
        modelValue: {
            type: Number as PropType<Nilable<number>>,
            required: false
        },
        system: {
            type: String as PropType<Nilable<string>>,
            required: false,
            default: 'metric'
        }
    });

    const emit = defineEmits([
        'update:modelValue'
    ]);

    const elFormObj = shallowRef<typeof Form>();
    const weightKg = ref<Nilable<number>>(null);
    const weightLb = ref<Nilable<number>>(null);

    function onChangeLb(val: string) {
        if (!lo_isNil(val)) {
            const newVal = parseFloat(val);

            if (!Number.isNaN(newVal)) {
                weightLb.value = newVal;
                convertToKg();
            }
        }

        updateInput();
    }

    function onChangeKg(val: string) {
        if (!lo_isNil(val)) {
            const newVal = parseFloat(val);

            if (!Number.isNaN(newVal)) {
                weightKg.value = newVal;
                convertToLb();
            }
        }

        updateInput();
    }

    function convertToKg() {
        if (lo_isNumber(weightLb.value)) {
            const valInKg = convertLbToKg(weightLb.value);
            weightKg.value = valInKg;
        } else {
            weightKg.value = null;
        }
    }

    function convertToLb() {
        if (lo_isNumber(weightKg.value)) {
            const valInLb = weightKg.value;
            weightLb.value = valInLb;
        } else {
            weightLb.value = null;
        }
    }

    async function updateInput() {
        const validateObj = await elFormObj.value?.validate();


        let input = validateObj.valid && lo_isNumber(weightKg.value)
            ? convertKgToGrams(weightKg.value)
            : null;
        
        emit('update:modelValue', input);
    }

    onBeforeMount(() => {
        const val = props.modelValue;

        if (lo_isNumber(val) && val > 0) {
            weightKg.value = convertGramsToKg(val);
            weightLb.value = convertGramsToLb(val);
        }
    });
</script>

<style lang="scss" scoped>
.ethnicity-partial {
    button {
        font-size: 160%;
    }
}
</style>
